import apiBolerplate from "utils/_helpers/apiBolerplate";
import axios from "utils/axios/axios";
import {
  SET_LOGOUT_USER,
  SET_ONBOARD_DATA,
  SET_USER_DETAILS,
} from "constants/auth/userConst";
import { history } from "utils/_helpers/history";
import { setChangePasswordDialog } from "../survey/surveyAction";
import { getNotification } from "actions/thread/threadAction";
import {
  setAuthData,
  getAuthData,
  clearAuthData,
  getToken,
  isTokenExpired,
  getPostLoginRedirect,
  clearPostLoginRedirect,
  cleanupOldAuthData,
} from "utils/encryption";
import { setLogin, setToken } from "../login/loginActions";

// ----------------- Actions -----------------

/**
 * Initialize auth state from encrypted localStorage synchronously
 * This is called on app load to set Redux state immediately
 * Returns user data for both user and login reducers
 */
export function initializeAuthFromLocalStorage() {
  const authData = getAuthData();

  if (!authData.token || !authData.user) {
    return null;
  }

  // Set axios header immediately
  axios.defaults.headers.common.Authorization = `Bearer ${authData.token}`;

  // Return complete user data with isLoggedin flag
  return {
    ...authData.user,
    isLoggedin: true,
  };
}

/**
 * Check and refresh token if expired
 * Returns true if token was refreshed, false if not needed
 */
export function checkAndRefreshTokenIfNeeded() {
  return async (dispatch) => {
    const token = getToken();

    // No token exists
    if (!token) {
      return false;
    }

    // Token is still valid, just set axios header
    if (!isTokenExpired()) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      return false;
    }

    // Token is expired, refresh it
    try {
      await dispatch(refresh_token());
      return true;
    } catch (error) {
      // Token refresh failed, clear auth and redirect to login
      clearAuthData();
      dispatch(setLogoutUser());
      history.navigate?.("/login");
      return false;
    }
  };
}

/**
 * Refresh the logged-in user's org_details from the server.
 *
 * org_details is a login-time snapshot of the org doc (encrypted localStorage
 * + redux). When ANOTHER admin of the same org edits Sector & Industry, this
 * user's copy stays stale until re-login — consumers like the materiality
 * Recommended filter (state.user.org_details.industry_id) then query with old
 * industries even after a page refresh, while the org structure page (fresh
 * via getParentEntity) shows the new values.
 *
 * /user/me returns a fresh org_details built by the same aggregation as
 * login, so merging it in (same pattern as updateEntityDetails /
 * EntityPopupMenu) heals every consumer on boot. Merge keeps client-added
 * keys (e.g. parent_id from the entity-switch fallback); a no-op result is
 * skipped to avoid needless dispatches.
 */
export function syncOrgDetails() {
  return async (dispatch, getState) => {
    apiBolerplate({
      dispatch,
      method: "get",
      url: `/user/me`,
      hideLoader: true,
      hideMsg: true,
      callback: (response) => {
        const fresh = response?.result?.org_details;
        if (!fresh) return;

        const user = getState()?.user || {};
        const mergedOrgDetails = { ...(user.org_details || {}), ...fresh };
        if (
          user.org_details &&
          JSON.stringify(mergedOrgDetails) === JSON.stringify(user.org_details)
        ) {
          return; // nothing drifted — skip the dispatch
        }

        const updatedUser = { ...user, org_details: mergedOrgDetails };
        dispatch(setUserDetails(updatedUser));
        setAuthData(getToken(), updatedUser);
      },
    });
  };
}

/**
 * Add user login - stores encrypted data and updates Redux
 */
export function addUserLogin(accessToken, userDetails) {
  return async (dispatch) => {
    // Store all auth data in one call
    setAuthData(accessToken, userDetails);

    // Set axios header
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    // Update user reducer
    dispatch(setUserDetails({ ...userDetails, isLoggedin: true }));

    // Update login reducer for backward compatibility
    dispatch(setLogin({
      ...userDetails,
      onBoarding: userDetails.onBoarding || false,
    }));
    dispatch(setToken(accessToken));
  };
}

/**
 * Navigate after login - checks for stored redirect or uses default
 */
function navigateAfterLogin(userDetails) {
  // Check if there's a stored redirect path
  const redirectPath = getPostLoginRedirect();

  if (redirectPath) {
    // Clear the stored redirect
    clearPostLoginRedirect();
    // Use stored redirect path
    history.navigate(redirectPath);
  } else {
    // No stored redirect, use default based on signup_process
    const signup_process = userDetails?.signup_process;

    switch (signup_process) {
      case 1:
        history.navigate("/verify");
        break;
      case 2:
      case 3:
        history.navigate("/onboard_details");
        break;
      default:
        // Fully authenticated - check if first time (show get_started) or returning (show dashboard)
        const completedSteps = JSON.parse(localStorage.getItem("completed_steps") || "[]");
        if (completedSteps.length === 0 && localStorage.getItem("current_organisation_user_type") === "SA") {
          history.navigate("/get_started");
        } else {
          history.navigate("/dashboard");
        }
        break;
    }
  }
}

export function userSignup(
  name,
  email,
  password,
  entity_name,
  redirectToVerify
) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/signup",
      messageType: "success",
      bodyData: { name, email, password, entity_name },
      callback: (data) => {
        dispatch(setUserDetails({ email: email, signup_process: 1 }));

        if (Boolean(redirectToVerify)) {
          redirectToVerify();
        }
      },
    });
  };
}

export function userLogin(email, password) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/login",
      bodyData: { email, password },
      messageType: "success",
      callback: (data) => {
        if (data?.result?.userDetails?.sts) {
          dispatch(setChangePasswordDialog(true));
        }
        dispatch(signupProcessLoginCase(data));
        dispatch(getNotification());
      },
    });
  };
}

export const signupProcessLoginCase = (data) => {
  return async (dispatch) => {
    const signup_process = data?.result?.userDetails?.signup_process;

    switch (signup_process) {
      case 1:
        dispatch(
          setUserDetails({
            ...data?.result?.userDetails,
            isLoggedin: false,
          })
        );
        history.navigate("/verify");
        break;

      case 2:
      case 3:
        dispatch(
          addUserLogin(data?.result?.accessToken, data?.result?.userDetails)
        );
        // Use navigateAfterLogin to support deep linking
        navigateAfterLogin(data?.result?.userDetails);
        break;

      default:
        dispatch(
          addUserLogin(data?.result?.accessToken, data?.result?.userDetails)
        );
        // Use navigateAfterLogin for fully authenticated users
        navigateAfterLogin(data?.result?.userDetails);
        break;
    }
  };
};

export function userVerifyOTP(email, otp) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/verify_otp",
      bodyData: { email, otp },
      messageType: "success",
      callback: (data) => {
        dispatch(
          addUserLogin(data?.result?.accessToken, data?.result?.userDetails)
        );
        // After OTP verification, go to onboarding (signup_process becomes 2)
        history.navigate("/onboard_details");
      },
    });
  };
}

export function userLogout() {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/logout",
      messageType: "success",
      callback: (data) => {
        clearAuthData();
        dispatch(setLogoutUser());
        history.navigate("/login");
      },
    });
  };
}

export function get_user_details(navigateUser = true, extraFunc) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/me",
      method: "get",
      hideMsg: true,
      callback: (data) => {
        const userDetails = data?.result;

        // Update encrypted storage (preserve token, update user data)
        const currentToken = getToken();
        if (currentToken) {
          setAuthData(currentToken, userDetails);
        }

        // Update user reducer
        dispatch(
          setUserDetails({
            ...userDetails,
            isLoggedin: true,
          })
        );

        // Update login reducer with properly mapped data
        dispatch(setLogin({
          ...userDetails,
          onBoarding: userDetails.onBoarding || false,
        }));

        dispatch(getNotification());

        const signup_process = userDetails?.signup_process;

        if (navigateUser) {
          switch (signup_process) {
            case 1:
              history.navigate("/verify");
              break;
            case 2:
            case 3:
              history.navigate("/onboard_details");
              break;
            default:
              break;
          }
        }

        if (Boolean(extraFunc)) {
          extraFunc();
        }
      },
    });
  };
}

export function refresh_token() {
  return async (dispatch) => {
    const data = await apiBolerplate({
      dispatch,
      url: "/user/refresh",
      hideMsg: true,
      callback: (data) => {
        const newToken = data.result.accessToken;
        axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;

        // Get current user data and update token
        const authData = getAuthData();
        if (authData.user) {
          setAuthData(newToken, authData.user);
        }

        dispatch(get_user_details(false));
      },
    });

    return data?.result?.accessToken;
  };
}

export function addOnBoardData(field, value) {
  return async (dispatch, getState) => {
    const { onBoardData } = getState().user;
    dispatch(setOnBoardData({ ...onBoardData, [field]: value }));
  };
}

export function createParentEntity(
  entity_id,
  entity_name,
  sector_id,
  industry_id,
  isin,
  setStep,
  setHasChanges
) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/entity/create_parent_entity",
      bodyData: { entity_id, entity_name, sector_id, industry_id, isin },
      callback: (data) => {
        dispatch(get_user_details({ navigateUser: false }));
        if (Boolean(setStep) && Boolean(setHasChanges)) {
          setStep((prev) => prev + 1);
          setHasChanges(false);
        }
      },
    });
  };
}

export function forGotPassowrd(email) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/forgot_password",
      messageType: "success",
      bodyData: { email },
    });
  };
}

export function resetPassword(reset_code, new_password, confirm_new_password) {
  return async (dispatch) => {
    await apiBolerplate({
      url: "/user/reset_password",
      bodyData: { reset_code, new_password, confirm_new_password },
      hideLoader: false,
      hideMsg: false,
      messageType: "success",
      callback: (data) => {
        if (data.status === true) {
          history.navigate("/login");
        }
      },
    });
  };
}

export function resendOtp(email) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/resend_otp",
      messageType: "success",
      bodyData: { email },
    });
  };
}

export function changePassword(name, new_password, confirm_new_password) {
  return async (dispatch) => {
    apiBolerplate({
      dispatch,
      url: "/user/change_password_for_new_user",
      bodyData: { name, new_password, confirm_new_password },
      hideLoader: false,
      hideMsg: false,
      messageType: "success",
    });
  };
}

export function userUserData(
  work,
  category,
  brief_on_your_role,
  goals,
  how_you_found_us,
  entity_id,
  setStep,
  setHasChanges
) {
  return async (dispatch) => {
    let bodyData = { entity_id, goals, how_you_found_us };
    if (work) bodyData.work = work;
    if (category) bodyData.category = category;
    if (brief_on_your_role) bodyData.brief_on_your_role = brief_on_your_role;
    apiBolerplate({
      dispatch,
      url: "/user/update_user",
      bodyData: bodyData,
      callback: (data) => {
        if (Boolean(setStep) && Boolean(setHasChanges)) {
          setStep((prev) => prev + 1);
          setHasChanges(false);
        }
      },
    });
  };
}

// Helper to set onboard data
export function setOnBoardData(payload) {
  return {
    type: SET_ONBOARD_DATA,
    payload: payload,
  };
}

// Helper to set user details
export function setUserDetails(payload) {
  return {
    type: SET_USER_DETAILS,
    payload: payload,
  };
}

// Helper to set logout user
export function setLogoutUser(payload) {
  return {
    type: SET_LOGOUT_USER,
    payload: payload,
  };
}
